import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, Link2, Lock, Trash2 } from 'lucide-react';
import type { ShareLink } from '../../shared/types';
import { api, del, patch, post } from '../lib/api';
import { formatDate, relativeTime } from '../lib/format';
import { errorMessage, useToast } from './toast';
import { Dialog, Field, Notice } from './ui';

const EXPIRY = [
  { value: '', label: 'Unbegrenzt' },
  { value: '7', label: '7 Tage' },
  { value: '30', label: '30 Tage' },
  { value: '90', label: '90 Tage' },
];

export function ShareDialog({
  open,
  onClose,
  campaignId,
  visibleToClient,
}: {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  visibleToClient: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const key = ['shares', campaignId];
  const links = useQuery({
    queryKey: key,
    queryFn: () => api<ShareLink[]>(`/api/campaigns/${campaignId}/shares`),
    enabled: open,
  });
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [expiry, setExpiry] = useState('');
  const [canComment, setCanComment] = useState(true);
  const [canApprove, setCanApprove] = useState(true);
  const [canDownload, setCanDownload] = useState(false);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link kopiert.');
    } catch {
      toast(url);
    }
  };

  const create = useMutation({
    mutationFn: () =>
      post<ShareLink>(`/api/campaigns/${campaignId}/shares`, {
        label,
        password: password || null,
        canComment,
        canApprove,
        canDownload,
        expiresAt: expiry ? new Date(Date.now() + Number(expiry) * 86400_000).toISOString() : null,
      }),
    onSuccess: async (link) => {
      setLabel('');
      setPassword('');
      await queryClient.invalidateQueries({ queryKey: key });
      await copy(link.url);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      patch(`/api/shares/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/api/shares/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  return (
    <Dialog open={open} onClose={onClose} title="Teilen & Freigabe-Links" wide>
      <p className="muted">
        Freigabe-Links funktionieren ohne Konto. Gäste sehen nie interne Kommentare. Mit Passwort und
        Ablaufdatum bleibt der Zugang kontrollierbar.
        {!visibleToClient &&
          ' Kundenkonten sehen die Kampagne erst, wenn sie unter „Bearbeiten“ sichtbar geschaltet ist.'}
      </p>

      <div className="card card-pad stack" style={{ background: 'var(--surface-2)', border: 0 }}>
        <strong>Neuen Link erstellen</strong>
        <div className="form-grid two">
          <Field label="Bezeichnung" hint="z. B. „Marketing-Team Kunde“">
            {(id) => (
              <input
                id={id}
                className="input"
                value={label}
                maxLength={120}
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </Field>
          <Field label="Passwort (optional)" hint="Mindestens 6 Zeichen">
            {(id) => (
              <input
                id={id}
                className="input"
                type="text"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="row-wrap" style={{ gap: 18 }}>
          <label className="check">
            <input type="checkbox" checked={canComment} onChange={(e) => setCanComment(e.target.checked)} />{' '}
            Kommentieren
          </label>
          <label className="check">
            <input type="checkbox" checked={canApprove} onChange={(e) => setCanApprove(e.target.checked)} />{' '}
            Freigeben
          </label>
          <label className="check">
            <input type="checkbox" checked={canDownload} onChange={(e) => setCanDownload(e.target.checked)} />{' '}
            Herunterladen
          </label>
          <div className="spacer" />
          <select
            className="select"
            style={{ width: 'auto' }}
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            aria-label="Gültigkeit"
          >
            {EXPIRY.map((o) => (
              <option key={o.value} value={o.value}>
                Gültig: {o.label}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            disabled={create.isPending || (password.length > 0 && password.length < 6)}
            onClick={() => create.mutate()}
          >
            <Link2 size={17} /> Link erstellen & kopieren
          </button>
        </div>
      </div>

      {links.error && <Notice kind="error">{errorMessage(links.error)}</Notice>}
      <div className="stack" style={{ gap: 0 }}>
        {links.data?.map((link) => {
          const inactive =
            Boolean(link.revokedAt) || Boolean(link.expiresAt && new Date(link.expiresAt) < new Date());
          return (
            <div
              key={link.id}
              className="row-wrap"
              style={{ padding: '14px 0', borderTop: '1px solid var(--line)', opacity: inactive ? 0.55 : 1 }}
            >
              <div className="grow stack" style={{ gap: 4 }}>
                <div className="row-wrap">
                  <strong>{link.label || 'Freigabe-Link'}</strong>
                  {link.hasPassword && (
                    <span className="chip chip-outline">
                      <Lock /> Passwort
                    </span>
                  )}
                  {link.revokedAt && <span className="chip chip-changes_requested">Deaktiviert</span>}
                  {link.expiresAt && (
                    <span className="chip chip-outline">bis {formatDate(link.expiresAt)}</span>
                  )}
                </div>
                <span className="mono small" style={{ wordBreak: 'break-all' }}>
                  {link.url}
                </span>
                <span className="tiny muted">
                  {[
                    link.canComment && 'Kommentieren',
                    link.canApprove && 'Freigeben',
                    link.canDownload && 'Herunterladen',
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Nur ansehen'}{' '}
                  · {link.accessCount} Aufrufe
                  {link.lastAccessAt ? `, zuletzt ${relativeTime(link.lastAccessAt)}` : ''}
                </span>
              </div>
              <div className="row">
                <button className="btn btn-outline btn-sm" onClick={() => copy(link.url)}>
                  <Copy size={15} /> Kopieren
                </button>
                <a
                  className="btn btn-ghost btn-sm btn-icon"
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Öffnen"
                >
                  <ExternalLink size={16} />
                </a>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => update.mutate({ id: link.id, body: { revoked: !link.revokedAt } })}
                >
                  {link.revokedAt ? 'Aktivieren' : 'Deaktivieren'}
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  aria-label="Löschen"
                  onClick={() => remove.mutate(link.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
        {links.data?.length === 0 && <p className="small muted">Noch keine Links für diese Kampagne.</p>}
      </div>
    </Dialog>
  );
}
